import React, { useEffect, useMemo, useState } from 'react'
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  ColumnDef,
  VisibilityState,
} from '@tanstack/react-table'

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'

import { PurchaseOrder } from '@/interfaces/purchaseOrder'
import { useTableContext } from './TableContext'
import { HeaderActions } from './HeaderActions'
import { Navigation } from './Navigation'

interface DataTableProps {
  columns: ColumnDef<PurchaseOrder>[]
  showEditPurchaseOrder: boolean
  showDeletePurchaseOrder: boolean
  onClose: () => void
  onCloseDelete: () => void
  purchaseOrderToEdit?: PurchaseOrder
  purchaseOrderToDelete?: string
  onNewPurchaseOrder: () => void
}

const PURCHASE_ORDER_TABLE_COLUMNS_STORAGE_KEY = 'purchase-order-table-columns'

export const DataTable: React.FC<DataTableProps> = ({
  columns,
  showEditPurchaseOrder,
  showDeletePurchaseOrder,
  onClose,
  onCloseDelete,
  purchaseOrderToEdit,
  purchaseOrderToDelete,
  onNewPurchaseOrder,
}) => {
  const { purchaseOrderData, isLoading } = useTableContext()

  // Column visibility state with localStorage persistence
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(() => {
    const saved = localStorage.getItem(PURCHASE_ORDER_TABLE_COLUMNS_STORAGE_KEY)
    return saved ? JSON.parse(saved) : {}
  })

  // Save column visibility to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(
      PURCHASE_ORDER_TABLE_COLUMNS_STORAGE_KEY,
      JSON.stringify(columnVisibility)
    )
  }, [columnVisibility])

  const data = useMemo(() => {
    return purchaseOrderData?.data || []
  }, [purchaseOrderData?.data])

  const table = useReactTable({
    data: data as PurchaseOrder[],
    columns,
    getCoreRowModel: getCoreRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    state: {
      columnVisibility,
    },
  })

  return (
    <div className="w-full">
      <div className="rounded-md border bg-white flex flex-col">
        <HeaderActions 
          columns={table.getAllColumns()} 
          onNewPurchaseOrder={onNewPurchaseOrder}
        />
        <div className="overflow-auto flex-1 relative">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead key={header.id} className="whitespace-nowrap">
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                    )
                  })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={index}>
                    {table.getAllColumns().map((column) => (
                      <TableCell key={column.id} className="py-3">
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  className="hover:bg-muted/50"
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="py-3">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No se encontraron órdenes de compra.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <Navigation />
      </div>
    </div>
  )
}
